import { useState } from "react";
import { Button } from "./Button";
import { cx, stagger } from "./util";

export interface RoomCodeProps {
  code: string;
  /** Show copy / share controls. Off for the compact nav treatment. */
  shareable?: boolean;
  size?: "large" | "compact";
  className?: string;
  onCopied?: (message: string) => void;
}

/**
 * The room code is the whole onboarding flow, so it is rendered as an
 * inset readout with the characters split apart — read aloud across a room
 * without misreads. The alphabet already excludes vowels and 0/O/1/I/L.
 */
export function RoomCode({
  code,
  shareable = true,
  size = "large",
  className,
  onCopied,
}: RoomCodeProps) {
  const [busy, setBusy] = useState(false);
  const joinUrl = `${window.location.origin}/?room=${code}`;

  async function copy() {
    setBusy(true);
    try {
      await navigator.clipboard.writeText(joinUrl);
      onCopied?.("Invite link copied.");
    } catch {
      onCopied?.(`Copy failed. The code is ${code}.`);
    } finally {
      setBusy(false);
    }
  }

  async function share() {
    if (!navigator.share) return copy();
    try {
      await navigator.share({
        title: "Badly Dubbed",
        text: `Join my game. Room code ${code}.`,
        url: joinUrl,
      });
    } catch {
      /* User dismissed the sheet. Nothing to report. */
    }
  }

  return (
    <div className={cx("bd-roomcode", size === "compact" && "is-compact", className)}>
      <div className="t-label muted">Room code</div>
      <div className="inset chamfer-sm bd-roomcode-readout">
        <output
          className={cx("t-display", "t-outlined", "bd-roomcode-chars")}
          aria-label={`Room code ${code.split("").join(" ")}`}
        >
          {code.split("").map((ch, i) => (
            <span
              key={`${ch}-${i}`}
              className="bd-roomcode-char bd-pop"
              style={stagger(i)}
              aria-hidden="true"
            >
              {ch}
            </span>
          ))}
        </output>
      </div>
      {shareable ? (
        <div className="cluster bd-roomcode-actions">
          <Button variant="primary" onClick={copy} disabled={busy}>
            Copy link
          </Button>
          {"share" in navigator ? (
            <Button variant="secondary" onClick={share}>
              Share
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
